import React from 'react';
import { useTranslation } from 'react-i18next';
import LOCALS from 'commons/locals';

export type CashSettlementConfirmSummary = {
  beginningCash: number;
  treasuryCashInAmount: number;
  treasuryCashOutAmount: number;
  treasuryCashMovementNet: number;
  systemCashSales: number;
  expectedCash: number;
  actualCash: number;
  variance: number;
  note: string;
  cashCounter: string;
};

type CashSettlementConfirmModalProps = {
  summary: CashSettlementConfirmSummary;
  currencySymbol: string;
  submitting: boolean;
  formatAmount: (amount: number | null | undefined) => string;
  onCancel: () => void;
  onConfirm: () => void;
};

const CashSettlementConfirmModal: React.FC<
  CashSettlementConfirmModalProps
> = ({
  summary,
  currencySymbol,
  submitting,
  formatAmount,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="p-6">
          <h3 className="text-xl font-medium text-gray-900 mb-6 pb-3 border-b">
            {t(LOCALS.daily_register_close_confirmation_title)}
          </h3>

          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_opening_cash)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.beginningCash)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_cash_in_from_safe)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.treasuryCashInAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_cash_out_to_safe)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.treasuryCashOutAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_cash_movement_net)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.treasuryCashMovementNet)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_system_cash_net)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.systemCashSales)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_expected_cash)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.expectedCash)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {t(LOCALS.daily_register_close_counted_cash)}
              </span>
              <span className="text-gray-900 font-medium">
                {currencySymbol} {formatAmount(summary.actualCash)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t pt-4">
              <span className="text-gray-700 font-medium">
                {t(LOCALS.daily_register_close_over_short_amount)}
              </span>
              <span
                className={`font-medium text-lg ${
                  summary.variance < 0
                    ? 'text-red-600'
                    : summary.variance > 0
                    ? 'text-green-600'
                    : 'text-gray-600'
                }`}
              >
                {currencySymbol} {formatAmount(Math.abs(summary.variance))}{' '}
              </span>
            </div>
            {summary.note && (
              <div className="border-t pt-4">
                <span className="text-gray-700 font-medium block mb-2">
                  {t(LOCALS.daily_register_close_over_short_note)}
                </span>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md">
                  {summary.note}
                </p>
              </div>
            )}
            <div className="border-t pt-4">
              <span className="text-gray-700 font-medium block mb-2">
                {t(LOCALS.daily_register_close_counted_by)}
              </span>
              <span className="text-gray-900">{summary.cashCounter}</span>
            </div>
          </div>

          <div className="flex space-x-4 pt-2">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 h-12 px-6 rounded-lg font-medium transition-colors"
            >
              {t(LOCALS.cancel)}
            </button>
            <button
              onClick={onConfirm}
              disabled={submitting}
              className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white h-12 px-6 rounded-lg font-medium transition-colors"
            >
              {submitting ? t(LOCALS.submitting) : t(LOCALS.confirm)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashSettlementConfirmModal;
